import { useEffect, useMemo, useState } from 'react';

import Select from 'react-select';

import Spinner from '../../components/reusable/spinner/spinner.component';
import Client from '../../tools/client';

import IntegrationTypeDisplay from '../../components/integration/display/integration-type-display.component';
import IntegrationConfigurationDisplay from '../../components/integration/display/integration-configuration-display.component';
import IntegrationSecretDisplay from '../../components/integration/display/integration-secret-display.component';
import IntegrationTestManager from '../../components/integration/test-manager/integration-test-manager.component';

import {
  MainContainer,
  MainTitle,
  TabContainer,
  TabSelector,
} from '../../styles/page.styles';

import {
  FieldSection,
  FieldGroup,
  Label,
  Note,
} from '../../components/configuration/sections/configuration-sections.styles';

const client = new Client();

const TAB_ORDER = ['email', 'sms', 'shipping', 'captcha'];

const IntegrationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [integrationTypes, setIntegrationTypes] = useState([]);
  const [currentTab, setCurrentTab] = useState('email');
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [publicKey, setPublicKey] = useState(null);

  const getIntegrations = async () => {
    setLoading(true);
    try {
      const res = await client.getIntegrationSetup();
      const filtered = res
        .filter(t => TAB_ORDER.includes(t.name))
        .sort((a, b) => TAB_ORDER.indexOf(a.name) - TAB_ORDER.indexOf(b.name));
      setIntegrationTypes(filtered);
    } catch (err) {
      console.error('Failed to load integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getIntegrations();
  }, []);

  useEffect(() => {
    const load = async () => {
      const type = integrationTypes.find(t => t.name === currentTab);
      if (!type) return;

      setLoading(true);
      try {
        const keyData = await client.getEncryptionSecret();
        setPublicKey(keyData.key);

        const activeId = selectedIntegrationId ?? type.configuration?.provider?.id;
        const integrations = type.integrations || [];
        const defaultIntegration =
          integrations.find(i => i.id === activeId) || integrations[0];

        if (!defaultIntegration) return;

        setSelectedIntegrationId(defaultIntegration.id);
        setIntegration(defaultIntegration);
      } catch (err) {
        console.error('Failed to load integration config:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [integrationTypes, currentTab]);

  const handleIntegrationChange = (option) => {
    const id = option.value;
    const currentType = integrationTypes.find(t => t.name === currentTab);
    const i = currentType?.integrations?.find(int => int.id === id);
    setSelectedIntegrationId(id);
    setIntegration(i);
  };

  const handleConfigUpdate = () => {
    getIntegrations();
  };

  const handleSecretUpdate = () => {
    getIntegrations();
  };

  const getProviderConfig = () => {
    const type = integrationTypes.find(t => t.name === currentTab);
    const config = type?.configuration || {};
    const prefix = `integrations.${type?.key}.${integration?.service}`;
    return Object.entries(config).reduce((acc, [key, value]) => {
      if (key.startsWith(prefix)) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  const updateIntegrationSecrets = (integrationId, updatedSecrets) => {
    setIntegration(prev => {
      if (!prev || prev.id !== integrationId) return prev;
      return { ...prev, secrets: updatedSecrets };
    });

    setIntegrationTypes(prev => {
      return prev.map(type => {
        if (!type.integrations) return type;

        const updatedIntegrations = type.integrations.map(i => {
          if (i.id !== integrationId) return i;
          return { ...i, secrets: updatedSecrets };
        });

        const requiredKeys = type.requiredKeys || [];
        const updated = updatedIntegrations.find(i => i.id === integrationId);
        const hasAllKeys = requiredKeys.every(key =>
          updated?.secrets?.some(s => s.keyName === key.keyName)
        );

        const config = { ...type.configuration };
        if (hasAllKeys) {
          config.provider = { id: integrationId };
        } else if (config.provider?.id === integrationId) {
          delete config.provider;
        }

        return {
          ...type,
          integrations: updatedIntegrations,
          configuration: config,
        };
      });
    });
  };

  const renderCurrentTab = () => {
    const integrationType = integrationTypes.find(t => t.name === currentTab);
    if (!integrationType || !integration) return null;

    const isActive = integration.id === integrationType.configuration?.provider?.id;
    const providerConfig = getProviderConfig();
    const selectOptions = (integrationType.integrations || []).map(i => ({
      value: i.id,
      label: i.name,
    }));

    return (
      <>
        <IntegrationTypeDisplay integrationType={integrationType} onUpdate={getIntegrations} />

        <FieldSection>
          <FieldGroup>
            <Label>Provider Details</Label>
            <Select
              value={selectOptions.find(opt => opt.value === selectedIntegrationId)}
              onChange={handleIntegrationChange}
              options={selectOptions}
            />
          </FieldGroup>

          {['ses', 'mailgun', 'postmark'].includes(integration?.service) && (
            <Note style={{ marginTop: '2rem', marginBottom: '1rem', color: '#c87d00' }}>
              This integration is available but <strong>has not been fully verified</strong>.
              Some providers may restrict access for new accounts. Use at your own discretion.
            </Note>
          )}

          <Note style={{ marginTop: '-0.5rem', marginBottom: '1rem' }}>
            {isActive ? (
              <span style={{ color: 'green' }}>
                This is the current provider for the {integrationType.name} integration.
              </span>
            ) : (
              <span>
                This is not the current provider for the {integrationType.name} integration.
              </span>
            )}
          </Note>

          {Object.keys(providerConfig).length > 0 && (
            <IntegrationConfigurationDisplay
              integration={integration}
              initialConfig={providerConfig}
              onUpdate={handleConfigUpdate}
            />
          )}

          <IntegrationSecretDisplay
            integration={integration}
            publicKey={publicKey}
            onSecretChange={updateIntegrationSecrets}
          />

          <IntegrationTestManager integration={integration} />
        </FieldSection>
      </>
    );
  };

  return (
    <MainContainer>
      <MainTitle>Integrations</MainTitle>
      <TabContainer>
        {integrationTypes.map((type) => (
          <TabSelector
            key={type.name}
            active={currentTab === type.name}
            onClick={() => setCurrentTab(type.name)}
          >
            {type.displayName || type.name}
          </TabSelector>
        ))}
      </TabContainer>
      {loading ? <Spinner /> : renderCurrentTab()}
    </MainContainer>
  );
};

export default IntegrationsPage;